import { useCompanySwitcher } from "@/hooks/useCompanySwitcher";
import { Building2, Check, ChevronsUpDown, Plus } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface CompanySwitcherProps {
  variant?: "sidebar" | "mobile";
}

export function CompanySwitcher({ variant = "sidebar" }: CompanySwitcherProps) {
  const { companies, activeCompany, activeCompanyId, switchCompany, isSwitching } = useCompanySwitcher();
  const { isSystemOwner, isAdmin, isCompanyOwner } = useUserRole();
  const navigate = useNavigate();
  // Owners (system_owner, admin, company_owner) can register additional
  // companies. The NewCompany page double-checks the role server-side.
  const canCreateCompany = isSystemOwner || isAdmin || isCompanyOwner;

  if (companies.length === 0 && !canCreateCompany) return null;

  if (variant === "mobile") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 px-4 py-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors active:scale-[0.97] w-full max-w-xs">
            <Building2 className="h-5 w-5 text-primary shrink-0" />
            <span className="text-sm font-medium truncate flex-1 text-left">
              {activeCompany?.name ?? "Välj företag"}
            </span>
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-56">
          <DropdownMenuLabel className="text-xs text-muted-foreground">Företag</DropdownMenuLabel>
          {companies.map((c) => (
            <DropdownMenuItem
              key={c.id}
              onClick={() => switchCompany(c.id)}
              className="gap-2"
              disabled={isSwitching}
            >
              <Building2 className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{c.name}</span>
              {c.planName && (
                <Badge
                  variant={c.subscriptionStatus === "active" ? "default" : "secondary"}
                  className="text-[10px] px-1.5 py-0 shrink-0"
                >
                  {c.planName}
                </Badge>
              )}
              {c.id === activeCompanyId && (
                <Check className="h-4 w-4 shrink-0 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
          {canCreateCompany && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/new-company")} className="gap-2">
                <Plus className="h-4 w-4 shrink-0" />
                <span>Nytt företag</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button className="flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 bg-accent-soft/60 hover:bg-accent-soft text-accent-ink hover:text-accent-ink w-full border border-border">
              <Building2 className="h-5 w-5 mb-0.5 text-accent" />
              <span className="text-[10px] font-semibold text-center leading-tight max-w-full truncate">
                {activeCompany?.name
                  ? activeCompany.name.length > 8
                    ? activeCompany.name.slice(0, 7) + "."
                    : activeCompany.name
                  : "Företag"}
              </span>
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p>{activeCompany?.name ?? "Välj företag"}</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent side="left" align="start" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Företag</DropdownMenuLabel>
        {companies.map((c) => (
          <DropdownMenuItem
            key={c.id}
            onClick={() => switchCompany(c.id)}
            className="gap-2"
            disabled={isSwitching}
          >
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate">{c.name}</span>
            {c.planName && (
              <Badge
                variant={c.subscriptionStatus === "active" ? "default" : "secondary"}
                className="text-[10px] px-1.5 py-0 shrink-0"
              >
                {c.planName}
              </Badge>
            )}
            {c.id === activeCompanyId && (
              <Check className="h-4 w-4 shrink-0 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
        {canCreateCompany && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/new-company")} className="gap-2">
              <Plus className="h-4 w-4 shrink-0" />
              <span>Nytt företag</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
